import { cn } from '@ifixit/ui/lib/utils';
import { useEffect } from 'react';
import type { HTMLAttributes } from 'react';

const INTEGRATION_SCRIPT_SRC = 'https://widgets.trustedshops.com/integration/integration.js';

export const TRUSTED_SHOPS_BADGE_FLAG = 'trusted-shops-badge';

// Per-store Trusted Shops integration IDs for the inline Trustbadge
// (https://github.com/iFixit/ifixit/issues/61178#issuecomment-4683958599).
// Each ID points at a remotely-managed widget config (the same
// inline-horizontal widget for every store) on
// trustbadge-config-api.trustedshops.com. The Next.js dev
// locales (eu_test, dev) map to the same integrations as their prod
// equivalents (eu, fr) so the badge is reachable in local dev without
// callers having to normalize.
const integrationIdByStoreCode: Record<string, string> = {
   eu: 'int-0534e257-f4e0-4f4b-97c4-b6309f6f9228',
   eu_test: 'int-0534e257-f4e0-4f4b-97c4-b6309f6f9228',
   de: 'int-bd86399e-13ea-4e55-9960-0e0b3fed3e24',
   fr: 'int-2c92a1de-0939-454b-89d2-318ba8d64d57',
   dev: 'int-2c92a1de-0939-454b-89d2-318ba8d64d57',
   uk: 'int-7a0dd964-c015-4376-8d8a-ebf38b1be629',
};

export function isTrustedShopsStore(storeCode: string): boolean {
   return storeCode in integrationIdByStoreCode;
}

declare module 'react' {
   // eslint-disable-next-line @typescript-eslint/no-namespace
   namespace JSX {
      interface IntrinsicElements {
         'minimized-trustbadge-inline-horizontal': HTMLAttributes<HTMLElement>;
      }
   }
}

export function TrustedShopsBadge({
   storeCode,
   className,
}: {
   storeCode: string;
   className?: string;
}) {
   const integrationId = integrationIdByStoreCode[storeCode];

   useEffect(() => {
      if (!integrationId) return;
      const script = document.querySelector<HTMLScriptElement>(
         `script[src="${CSS.escape(INTEGRATION_SCRIPT_SRC)}"]`
      );
      if (!script) {
         loadTrustbadgeScript(integrationId);
      } else if (script.dataset.integrationId !== integrationId) {
         script.dataset.integrationId = integrationId;
         void switchTrustbadgeShop(script, integrationId);
      }
   }, [integrationId]);

   if (!integrationId) {
      return null;
   }

   return (
      <div className={cn('mt-6 flex justify-center', className)}>
         <minimized-trustbadge-inline-horizontal />
      </div>
   );
}

// integration.js shows one shop per page. Its only redraw hook,
// trustbadgeIntegration.reload(), re-renders from the config object it fetched
// at startup, so we keep a handle on that object as it loads, and a store change
// without a page load rewrites it with the new shop and calls reload(). If a
// Trusted Shops release stops reusing that object, switching does nothing and
// the badge keeps the first shop; a review list the shopper already opened keeps
// the first shop's reviews either way. Replace with their API if they add one.
const trustbadge: { config?: Record<string, unknown>; fetch?: typeof window.fetch } = {};

type TrustbadgeWindow = Window & { trustbadgeIntegration?: { reload?: () => void } };

function trustbadgeConfigUrl(integrationId: string) {
   return `https://widgets.trustedshops.com/js/${integrationId}.json`;
}

function loadTrustbadgeScript(integrationId: string) {
   const script = document.createElement('script');
   script.type = 'module';
   script.src = INTEGRATION_SCRIPT_SRC;
   script.dataset.integrationId = integrationId;
   // integration.js requests its config while it first runs, before `load`.
   const stopCapture = captureTrustbadgeConfig(script);
   script.addEventListener('load', stopCapture, { once: true });
   script.addEventListener('error', stopCapture, { once: true });
   document.head.append(script);
}

// Watches fetch for the config request integration.js makes for the ID it
// reads from `script`. Returns a function that stops watching.
function captureTrustbadgeConfig(script: HTMLScriptElement) {
   const originalFetch = window.fetch;
   trustbadge.fetch = originalFetch;
   window.fetch = captureFetch;
   return stop;

   async function captureFetch(input: RequestInfo | URL, init?: RequestInit) {
      const href = input instanceof Request ? input.url : String(input);
      if (!href.startsWith(trustbadgeConfigUrl(script.dataset.integrationId ?? ''))) {
         return originalFetch(input, init);
      }
      stop();
      const response = await originalFetch(input, init);
      if (!response.ok) return response;
      try {
         const config = await response.clone().json();
         trustbadge.config = config;
         response.json = () => Promise.resolve(config);
      } catch (error) {
         // integration.js still reads the untouched response itself.
         console.error(
            'Trusted Shops badge: could not read the integration.js config, so it cannot switch shops',
            error
         );
      }
      return response;
   }

   function stop() {
      if (window.fetch === captureFetch) window.fetch = originalFetch;
   }
}

async function switchTrustbadgeShop(script: HTMLScriptElement, integrationId: string) {
   try {
      // Goes around the watcher, so it only ever captures integration.js's request.
      const pageFetch = trustbadge.fetch ?? window.fetch;
      const response = await pageFetch(trustbadgeConfigUrl(integrationId));
      if (!response.ok) {
         logSwitchFailure(`config request returned ${response.status}`);
         return;
      }
      const config = await response.json();
      const { trustbadgeIntegration } = window as TrustbadgeWindow;
      // A later store change may have started its own switch.
      if (script.dataset.integrationId !== integrationId) return;
      // integration.js hasn't finished starting. If it hasn't read the tag yet,
      // it picks up the new ID itself.
      if (!trustbadgeIntegration?.reload) return;
      if (!trustbadge.config) {
         logSwitchFailure('never saw integration.js request its config');
         return;
      }
      for (const key of Object.keys(trustbadge.config)) {
         Reflect.deleteProperty(trustbadge.config, key);
      }
      Object.assign(trustbadge.config, config);
      trustbadgeIntegration.reload();
   } catch (error) {
      logSwitchFailure(error);
   }
}

// The badge keeps showing the shop it had.
function logSwitchFailure(reason: unknown) {
   console.error('Trusted Shops badge: could not switch shops', reason);
}
